import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { compact, groupBy, omit, uniq } from 'lodash';
import { type EnqueueSnackbar, useSnackbar } from 'notistack';
import { useState } from 'react';

import useHandleError from '@/hooks/useHandleError';
import {
  type ProductDTO,
  productsAtom,
  useFetchOrganizations
} from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

import { EMPTY_PRODUCT } from './ProductEditDialog.constants';
import type {
  ProductCreateRequestDTO,
  ProductEditDialogMode,
  ProductUpdateRequestDTO
} from './ProductEditDialog.types';

export const productEditDialogProdAtom = atom<ProductDTO>(EMPTY_PRODUCT);
const productEditDialogOpenAtom = atom(false);
const productEditDialogModeAtom = atom<ProductEditDialogMode>('New');

export const productSubscriptionUnitsAtom = atom((get) => {
  const subscriptionUnits: string[] = Object.keys(
    groupBy(get(productsAtom), 'subscription_unit')
  );
  return compact(uniq(subscriptionUnits)).sort();
});

const saveNewProduct = (prod: ProductCreateRequestDTO) =>
  axiosClient.post('/products/', prod);

const updateProduct = (prod: ProductUpdateRequestDTO, id: number) =>
  axiosClient.put(`/products/${id}`, prod);

const deleteProduct = (id: number) => axiosClient.delete(`/products/${id}`);

const showSnackbar = (
  enqueueSnackbar: EnqueueSnackbar,
  mode: ProductEditDialogMode
) => {
  let action = '';
  switch (mode) {
    case 'Edit':
      action = 'Updated';
      break;
    case 'Delete':
      action = 'Deleted';
      break;
    default:
      action = 'Saved';
  }
  enqueueSnackbar(`Product ${action}`, {
    variant: 'success'
  });
};

export const useProductDialog = () => {
  const [errorState, setErrorState] = useState<{
    message: string;
    product: ProductDTO;
  } | null>(null);
  const [mode, setMode] = useAtom(productEditDialogModeAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useAtom(productEditDialogOpenAtom);
  const { refetch } = useFetchOrganizations();
  const [product, setProduct] = useAtom(productEditDialogProdAtom);

  // Errors belong to the product they were raised for; a changed product
  // hides the stale error.
  const error = errorState?.product === product ? errorState.message : '';
  const setError = (message: string) => setErrorState({ message, product });

  const openDialog = (
    dialogMode: ProductEditDialogMode = 'New',
    prod: ProductDTO = EMPTY_PRODUCT
  ) => {
    setErrorState(null);
    setMode(dialogMode);
    setProduct(prod);
    setOpen(true);
  };

  const closeDialog = () => {
    setErrorState(null);
    setProduct(EMPTY_PRODUCT);
    setOpen(false);
  };

  const handleError = useHandleError();
  const mutation = useMutation({
    mutationFn: ({
      data,
      id
    }: {
      data: ProductCreateRequestDTO | ProductUpdateRequestDTO;
      id?: number;
    }) => {
      switch (mode) {
        case 'Edit':
          return updateProduct(data as ProductUpdateRequestDTO, id || -1);
        case 'Delete':
          return deleteProduct(id || -1);
        default: {
          const newProd = {
            ...data,
            salesforce_organization_id: -1
          };
          return saveNewProduct(
            omit(newProd, ['id']) as ProductCreateRequestDTO
          );
        }
      }
    },
    onSuccess: () => {
      closeDialog();
      showSnackbar(enqueueSnackbar, mode);
      refetch();
    },
    onError: (queryError: AxiosError) => {
      setError('Error executing operation');
      handleError(queryError, 'Failure to process product.');
    }
  });

  return {
    product,
    open,
    error,
    mutation,
    mode,
    setError,
    openDialog,
    closeDialog,
    setProduct
  };
};
